import { Formik } from 'formik';
import * as Yup from 'yup';
import { useAppointmentState, useAppointmentUpdateAndPersist } from '../../../../../context/Appointment/appointment';
import { CTAButton } from '../../../../../controls/ButtonBase';
import { useLocalization, useLocalizationExtension } from '../../../../../context/Localization/localization';
import { formatAsCurrency } from '../../../../../helpers/Number';
import { KeyManagementHeader } from '../../../../../controls/KeyManagementHeader/KeyManagementHeader';
import { KeyboardLayouts } from '../../../../../controls/SimpleKeyboard/Layout';
import { nameof } from '../../../../../helpers/nameof';
import { GMFormPriceField, GMFormRoNumberField } from '../../../../../controls/TextField';
import { GMButton } from '../../../../../controls/Button';
import { GMDialog, GMDialogYesNo } from '../../../../../controls/Dialog';
import { GMForm, GMFormTranslateErrors, GMFormWrapper } from '../../../../../controls/Form';
import { useRef, useState } from 'react';
import { GoMotoFunction } from '../../../../../API/GoMoto.types';
import { getROAppointment, IAppointment } from '../../../../../API/GoMoto';
import { getFullDisplayName } from '../../../../../helpers/Appointment';

const minMax = {
    min: 0.49,
    max: 100000.00,
};
const useValidationSchema = (ro?: boolean) => {
    const Localization = useLocalization();
    const { formatAsString, formatRequired, formatInvalid } = useLocalizationExtension();

    const formattedValues = {
        min: minMax.min.toLocaleString(undefined, { maximumFractionDigits: 2 }),
        max: minMax.max.toLocaleString(undefined, { maximumFractionDigits: 2 }),
    };

    const totalSchema = Yup.object()
        .shape({
            total: Yup.number()
                .required(formatRequired(Localization.PaymentAmount))
                .typeError(formatInvalid(Localization.PaymentAmount))
                .moreThan(minMax.min, formatAsString(Localization.ValueMustBeLessThanMaxAndGreaterThanMin, formattedValues))
                .lessThan(minMax.max, formatAsString(Localization.ValueMustBeLessThanMaxAndGreaterThanMin, formattedValues)),
        });

    if (ro) {
        return totalSchema.concat(
            Yup.object({
                roNumber: Yup.string()
                    .nullable()
                    .trim()
                    .required(formatRequired(Localization.RONumber)),
            }),
        );
    }

    return totalSchema;
};

export const UpdateTotalDialog = (props: { total: number | undefined; open: boolean; onClose: () => void; onConfirm: (newTotal: number | undefined) => void; }) => {
    const validationSchema = useValidationSchema();
    const Localization = useLocalization();

    return (
        <Formik
            initialValues={{ total: props.total?.toString() ?? '' }}
            onSubmit={(values) => {
                const newTotal: number | undefined = isNaN(+values.total) ? undefined : +values.total;

                if (newTotal !== props.total) {
                    props.onConfirm(newTotal);
                }
                else {
                    props.onClose();
                }
            }}
            validationSchema={validationSchema}
        >
            {({ values, submitForm }) => (
                <GMFormTranslateErrors>
                    <GMDialog
                        ariaLabel={Localization.UpdateTotal}
                        open={props.open}
                        title={Localization.UpdateTotal}
                        actions={[
                            <GMButton
                                id={Localization.Close}
                                title={Localization.Close}
                                ariaLabel={Localization.Close}
                                onClick={props.onClose}
                            >
                                {Localization.Close}
                            </GMButton>,
                            <GMButton
                                id={Localization.Submit}
                                title={Localization.Submit}
                                ariaLabel={Localization.Submit}
                                onClick={submitForm}
                            >
                                {Localization.Submit}
                            </GMButton>,
                        ]}
                    >
                        <div>
                            {Localization.CurrentTotal}{': '}{formatAsCurrency(props.total)}
                        </div>
                        <GMForm>
                            <GMFormWrapper finePrint requiredFieldLabel>{Localization.RequiredFields}</GMFormWrapper>
                            <GMFormPriceField
                                name={nameof<typeof values>('total')}
                                id={nameof<typeof values>('total')}
                                title={Localization.EnterNewTotal}
                                label={Localization.EnterNewTotal}
                                ariaLabel={Localization.EnterNewTotal}
                                autoFocus
                                required
                            />
                        </GMForm>
                    </GMDialog>
                </GMFormTranslateErrors>
            )}
        </Formik>
    );
};

export const UpdateTotalForm = (props: { onProceed: () => void; }) => {
    const validationSchema = useValidationSchema(true);
    const Localization = useLocalization();
    const appointment = useAppointmentState();
    const updateAppointment = useAppointmentUpdateAndPersist(true);

    const [showRoNumberWarningModal, setShowRoNumberWarningModal] = useState<boolean>(false);

    const shouldCancelRef = useRef<boolean>(false);
    const roNumberRef = useRef<IAppointment['roNumber'] | null>(null);
    const resolveModalPromise = useRef<GoMotoFunction<[void | PromiseLike<void>], void> | null>(null);
    const newAppointmentRef = useRef<IAppointment | null>(null);

    const initialValues = {
        total: appointment.serviceTotal?.toString() ?? '',
        roNumber: appointment.roNumber,
    };

    const waitForModalToClose: GoMotoFunction<void, Promise<void>> = () => {
        return new Promise<void>((resolve) => {
            resolveModalPromise.current = resolve;
        });
    };

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={async (values, { setSubmitting }) => {
                roNumberRef.current = values.roNumber;
                newAppointmentRef.current = await getROAppointment(values.roNumber ?? '', appointment.dealerKey);

                if (newAppointmentRef.current !== null) {
                    newAppointmentRef.current = {
                        ...newAppointmentRef.current,
                        businessName: newAppointmentRef.current?.businessName ?? '',
                        firstName: newAppointmentRef.current?.firstName ?? '',
                        lastName: newAppointmentRef.current?.lastName ?? '',
                    };
                }

                const hasBusinessNameChanged: boolean = newAppointmentRef.current?.businessName !== appointment.businessName;
                const hasCustomerNameChanged: boolean = newAppointmentRef.current?.firstName !== appointment.firstName ||
                    newAppointmentRef.current.lastName !== appointment.lastName;

                if (newAppointmentRef.current !== null && (hasBusinessNameChanged || hasCustomerNameChanged)) {
                    setShowRoNumberWarningModal(true);
                    await waitForModalToClose();
                }

                if (shouldCancelRef.current === true) {
                    shouldCancelRef.current = false;
                    setSubmitting(false);
                }
                else {
                    const newTotal: number | undefined = isNaN(+values.total) ? undefined : +values.total;

                    await updateAppointment({ ...appointment,
                        paymentStatus: 'not paid',
                        serviceTotal: newTotal,
                        roNumber: values.roNumber ? values.roNumber.trim() : values.roNumber }, false, false, false, true, undefined, false, false, false, false, false, true);

                    props.onProceed();
                }
            }}
            validationSchema={validationSchema}
        >
            <>
                <GMForm
                    topDivider
                    heading={<KeyManagementHeader secondaryHeading={Localization.EnterPaymentDetailsForPickup} leftAlignSecondaryHeading />}
                    submitButton={
                        <CTAButton
                            styleType={'primary'}
                            type={'submit'}
                            title={Localization.ClickToProceedToNextStep}
                            label={Localization.Proceed}
                            id={Localization.ClickToProceedToNextStep}
                            ariaLabel={Localization.ClickToProceedToNextStep}
                        />
                    }
                >
                    <GMFormWrapper finePrint requiredFieldLabel>{Localization.RequiredFields}</GMFormWrapper>
                    <GMFormPriceField
                        name={nameof<typeof initialValues>('total')}
                        id={nameof<typeof initialValues>('total')}
                        title={Localization.PaymentAmount}
                        label={Localization.PaymentAmount}
                        ariaLabel={Localization.PaymentAmount}
                        autoFocus
                        required
                    />
                    <GMFormRoNumberField
                        name={nameof<typeof initialValues>('roNumber')}
                        id={nameof<typeof initialValues>('roNumber')}
                        title={Localization.RONumber}
                        label={Localization.RONumber}
                        ariaLabel={Localization.RONumber}
                        required
                        keyboardLayout={KeyboardLayouts.AlphanumericNumpad}
                    />
                </GMForm>
                <GMDialogYesNo
                    yesText={Localization.Confirm}
                    onYes={() => {
                        setShowRoNumberWarningModal(false);

                        if (resolveModalPromise.current) {
                            resolveModalPromise.current();
                        }
                    }}
                    noText={Localization.GoBack}
                    onNo={() => {
                        setShowRoNumberWarningModal(false);
                        shouldCancelRef.current = true;

                        if (resolveModalPromise.current) {
                            resolveModalPromise.current();
                        }
                    }}
                    ariaLabel={Localization.RONumber}
                    open={showRoNumberWarningModal}
                >
                    {newAppointmentRef.current !== null ? (
                        Localization.formatString(
                            Localization.DoubleCheckingRoNumber,
                            roNumberRef.current ?? '',
                            getFullDisplayName(newAppointmentRef.current as IAppointment, false),
                            getFullDisplayName(appointment, false),
                        )
                    ) : null}
                </GMDialogYesNo>
            </>
        </Formik>
    );
};
